'use strict';

/*
 * product-hero.js
 * Variant selection, purchase type (Subscribe & Save / one-time), selling
 * plan wiring, and quantity controls for the Product Hero section.
 *
 * Variant + selling plan data is read from:
 *   <script type="application/json" id="product-variants-<sectionId>">
 * Each variant carries its selling_plan_allocations (plan id, price,
 * compare_at_price), so every subscription price and saving shown is
 * calculated from Shopify's selling plan data — never a fixed percentage.
 *
 * All other config is read from data attributes on the relevant DOM elements.
 */

(function () {

  // Locate variant data (may not exist if the product has no variants yet —
  // we still want the option selector + submit validation to work).
  var variantDataEl = document.querySelector('script[id^="product-variants-"]');

  // If neither variant data nor any option groups are present, this section
  // isn't on the page — bail.
  if (!variantDataEl && !document.querySelector('[data-option-index]')) return;

  var variants = [];
  if (variantDataEl) {
    try { variants = JSON.parse(variantDataEl.textContent); } catch (e) {}
  }

  // DOM refs
  var atcForm = document.querySelector('[data-atc-form]')
             || document.querySelector('form[action*="/cart/add"]');
  var variantInput      = atcForm ? atcForm.querySelector('input[name="id"]') : null;
  var submitBtn         = document.querySelector('[data-purchase-submit]');
  var btnLabel          = document.querySelector('[data-btn-label]');
  var btnCompare        = document.querySelector('[data-btn-compare]');
  var btnPrice          = document.querySelector('[data-btn-price]');
  var deliveryOptions   = document.querySelector('[data-delivery-options]');
  var purchaseRadio     = document.querySelector('[data-purchase-radio]');
  var radioOptions      = document.querySelectorAll('[data-purchase-radio] [data-radio-option]');
  var radioInputs       = document.querySelectorAll('[data-purchase-radio] input[name="purchase_type"]');
  var subscribeInput    = document.querySelector('[data-purchase-radio] input[value="subscribe"]');
  var onetimeInput      = document.querySelector('[data-purchase-radio] input[value="onetime"]');
  var subscribeOption   = document.querySelector('[data-radio-option="subscribe"]');
  var subscribeTitle    = document.querySelector('[data-subscribe-title]');
  var subscribeCompare  = document.querySelector('[data-subscribe-compare]');
  var subscribePrice    = document.querySelector('[data-subscribe-price]');
  var subscribeSavings  = document.querySelector('[data-subscribe-savings]');
  var onetimePriceEls   = document.querySelectorAll('[data-onetime-price]');
  var qtyInput          = document.querySelector('[data-qty-input]');
  var sellingPlanInput  = document.querySelector('[data-selling-plan-input]');
  var sellingPlanSelect = document.querySelector('[data-selling-plan-select]');
  var optionGroups      = document.querySelectorAll('[data-option-index]');

  var subscribeLabel = submitBtn ? (submitBtn.dataset.subscribeLabel || 'Add Subscription') : '';
  var onetimeLabel   = submitBtn ? (submitBtn.dataset.onetimeLabel   || 'Add to Cart')      : '';
  var soldOutLabel   = submitBtn ? (submitBtn.dataset.soldOutLabel   || 'Sold Out')         : '';

  // Label templates rendered by product-hero.liquid from section settings.
  // Tokens: [discount] (% saved), [amount] (money saved), [plan] (plan name).
  var config = purchaseRadio ? purchaseRadio.dataset : {};
  var moneyFormat = (variantDataEl && variantDataEl.dataset.moneyFormat) || '${{amount}}';

  var checkedRadio = document.querySelector('[data-purchase-radio] input[name="purchase_type"]:checked');
  var currentPurchaseType = checkedRadio ? checkedRadio.value : 'onetime';
  var initialVariantId = variantInput ? variantInput.value : null;

  // Selected options state — initialise from first active button in each group.
  // Nothing is pre-selected by default, so the customer must explicitly choose
  // both Size and Stoma Side before Add to Cart / Add Subscription enables.
  var selectedOptions = [];
  optionGroups.forEach(function (group) {
    var firstBtn = group.querySelector('.pdp-hero__option-btn--active');
    selectedOptions[parseInt(group.dataset.optionIndex, 10)] = firstBtn ? firstBtn.dataset.value : '';
  });

  // ── Add-to-Cart validation ────────────────────────────────────
  // We no longer disable the button. Instead, the submit handler below
  // intercepts clicks when selections are missing, marks each empty
  // option group with an error style + inline message, and lets the
  // customer correct it before the form actually posts.
  var requiredOptionCount = optionGroups.length;

  function allOptionsSelected() {
    if (requiredOptionCount === 0) return true;
    for (var i = 0; i < requiredOptionCount; i++) {
      if (!selectedOptions[i]) return false;
    }
    return true;
  }

  function clearGroupError(group) {
    if (!group) return;
    group.classList.remove('pdp-hero__option--error');
    var msg = group.querySelector('[data-option-error]');
    if (msg) {
      msg.hidden = true;
      msg.removeAttribute('hidden');
      msg.style.display = 'none';
    }
  }

  function markGroupError(group) {
    if (!group) return;
    group.classList.add('pdp-hero__option--error');
    var msg = group.querySelector('[data-option-error]');
    if (msg) {
      msg.hidden = false;
      msg.removeAttribute('hidden');
      msg.style.display = 'block';
    }
  }

  function clearAllErrors() {
    optionGroups.forEach(function (group) {
      // Walk up to the nearest .pdp-hero__option wrapper — that's where the
      // error class + inline message live.
      var wrap = group.closest('.pdp-hero__option') || group;
      clearGroupError(wrap);
    });
  }

  function flagMissingSelections() {
    var firstMissing = null;
    optionGroups.forEach(function (group) {
      var idx = parseInt(group.dataset.optionIndex, 10);
      var wrap = group.closest('.pdp-hero__option') || group;
      if (!selectedOptions[idx]) {
        markGroupError(wrap);
        if (!firstMissing) firstMissing = group;
      } else {
        clearGroupError(wrap);
      }
    });
    if (firstMissing) {
      try {
        firstMissing.scrollIntoView({ behavior: 'smooth', block: 'center' });
        var firstBtn = firstMissing.querySelector('.pdp-hero__option-btn');
        if (firstBtn) firstBtn.focus({ preventScroll: true });
      } catch (e) {}
    }
  }

  // ── Helpers ────────────────────────────────────────────────────

  // Formats cents using the shop's money format (e.g. "${{amount}}"),
  // so JS-updated prices match the Liquid `money` filter output.
  function formatMoney(cents) {
    var value = (cents || 0) / 100;
    function delimit(amount, decimals, thousands, decimal) {
      var parts = amount.toFixed(decimals).split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return parts.join(decimal);
    }
    return moneyFormat.replace(/\{\{\s*(\w+)\s*\}\}/, function (match, key) {
      switch (key) {
        case 'amount_no_decimals':                      return delimit(value, 0, ',', '.');
        case 'amount_with_comma_separator':             return delimit(value, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return delimit(value, 0, '.', ',');
        case 'amount_with_apostrophe_separator':        return delimit(value, 2, "'", '.');
        default:                                        return delimit(value, 2, ',', '.');
      }
    });
  }

  // Replaces [token] placeholders and tidies leftover whitespace (a token
  // can be blank, e.g. [discount] on a plan with no discount).
  function fillTemplate(template, values) {
    return (template || '')
      .replace(/\[(\w+)\]/g, function (match, key) {
        return Object.prototype.hasOwnProperty.call(values, key) ? values[key] : match;
      })
      .replace(/\s{2,}/g, ' ')
      .trim();
  }

  function findVariant() {
    if (!variants.length) return null;
    return variants.find(function (v) {
      return v.options.every(function (opt, i) {
        return selectedOptions[i] === undefined || opt === selectedOptions[i];
      });
    }) || null;
  }

  // Before the customer has picked every option, price against the
  // variant the page was rendered with (matches the Liquid output).
  function activeVariant() {
    return findVariant()
      || variants.find(function (v) { return String(v.id) === String(initialVariantId); })
      || variants[0]
      || null;
  }

  function allocationFor(variant, planId) {
    if (!variant || !planId || !variant.selling_plan_allocations) return null;
    return variant.selling_plan_allocations.find(function (a) {
      return String(a.selling_plan_id) === String(planId);
    }) || null;
  }

  // Savings come straight from the allocation: compare_at_price is the
  // price without the plan, price is the price with it.
  function pricing(variant, allocation) {
    var compare = allocation.compare_at_price || variant.price;
    var savings = Math.max(compare - allocation.price, 0);
    var pct     = compare > 0 ? Math.round(savings * 100 / compare) : 0;
    return {
      price:    allocation.price,
      compare:  compare,
      savings:  savings,
      discount: pct > 0 ? pct + '%' : '',
      amount:   savings > 0 ? formatMoney(savings) : ''
    };
  }

  // ── UI updates ─────────────────────────────────────────────────

  function render() {
    var variant = activeVariant();
    if (!variant) return;

    var planId     = sellingPlanSelect ? sellingPlanSelect.value : '';
    var allocation = allocationFor(variant, planId);
    var sub        = allocation ? pricing(variant, allocation) : null;

    // Subscription isn't offered for this variant + plan → fall back to one-time.
    if (subscribeInput) {
      subscribeInput.disabled = !sub;
      if (subscribeOption) subscribeOption.classList.toggle('pdp-purchase-radio__option--unavailable', !sub);
      if (!sub && currentPurchaseType === 'subscribe' && onetimeInput) {
        onetimeInput.checked = true;
        currentPurchaseType = 'onetime';
      }
    }

    if (sub) {
      if (subscribeTitle) subscribeTitle.textContent = fillTemplate(config.subscribeLabelTemplate, { discount: sub.discount });
      if (subscribePrice) subscribePrice.textContent = formatMoney(sub.price);
      if (subscribeCompare) {
        subscribeCompare.textContent = formatMoney(sub.compare);
        subscribeCompare.hidden = sub.savings <= 0;
      }
      if (subscribeSavings) {
        subscribeSavings.textContent = fillTemplate(config.savingsLabelTemplate, { amount: sub.amount, discount: sub.discount });
        subscribeSavings.hidden = sub.savings <= 0;
      }
    }

    // Each delivery option's text can include its own savings.
    if (sellingPlanSelect && config.planLabelTemplate) {
      Array.prototype.forEach.call(sellingPlanSelect.options, function (opt) {
        var a = allocationFor(variant, opt.value);
        var p = a ? pricing(variant, a) : { discount: '', amount: '' };
        opt.textContent = fillTemplate(config.planLabelTemplate, {
          plan: opt.dataset.planName || opt.textContent,
          discount: p.discount,
          amount: p.amount
        });
      });
    }

    onetimePriceEls.forEach(function (el) { el.textContent = formatMoney(variant.price); });

    // Purchase type UI
    var isSubscribe = currentPurchaseType === 'subscribe' && !!sub;
    radioOptions.forEach(function (opt) {
      opt.classList.toggle('pdp-purchase-radio__option--active', opt.dataset.radioOption === currentPurchaseType);
    });
    if (deliveryOptions) deliveryOptions.style.display = isSubscribe ? '' : 'none';

    // selling_plan sent to /cart/add: the chosen plan, or empty for one-time.
    if (sellingPlanInput) sellingPlanInput.value = isSubscribe ? planId : '';

    // Submit button
    if (submitBtn) {
      // Only out-of-stock variants disable the button. Missing-selection
      // validation is handled on submit, not by disabling.
      submitBtn.disabled = !variant.available;
      if (btnLabel) {
        btnLabel.textContent = !variant.available ? soldOutLabel : (isSubscribe ? subscribeLabel : onetimeLabel);
      }
      if (btnPrice) btnPrice.textContent = formatMoney(isSubscribe ? sub.price : variant.price);
      if (btnCompare) {
        btnCompare.textContent = sub ? formatMoney(sub.compare) + ' ' : '';
        btnCompare.hidden = !(isSubscribe && sub.savings > 0);
      }
    }
  }

  function updateVariantUI() {
    var variant = findVariant();
    if (variant && variantInput) variantInput.value = variant.id;
    render();
  }

  function setPurchaseType(type) {
    if (type === 'subscribe' && subscribeInput && subscribeInput.disabled) return;
    currentPurchaseType = type;
    radioInputs.forEach(function (input) { input.checked = input.value === type; });
    render();
  }

  // ── Event listeners ────────────────────────────────────────────

  // Variant option buttons
  optionGroups.forEach(function (group) {
    group.querySelectorAll('.pdp-hero__option-btn').forEach(function (btn) {
      btn.addEventListener('click', function () {
        group.querySelectorAll('.pdp-hero__option-btn').forEach(function (b) {
          b.classList.remove('pdp-hero__option-btn--active');
          b.setAttribute('aria-pressed', 'false');
        });
        btn.classList.add('pdp-hero__option-btn--active');
        btn.setAttribute('aria-pressed', 'true');
        selectedOptions[parseInt(group.dataset.optionIndex, 10)] = btn.dataset.value;
        // Clear this group's error now that a selection has been made.
        var wrap = group.closest('.pdp-hero__option') || group;
        clearGroupError(wrap);
        updateVariantUI();
      });
    });
  });

  // Submit interceptor — block /cart/add when selections are missing,
  // surface inline errors, scroll/focus the first missing group.
  // Attached to the form directly so it works regardless of whether
  // a specific button element is found.
  if (atcForm) {
    atcForm.addEventListener('submit', function (e) {
      if (!allOptionsSelected()) {
        e.preventDefault();
        e.stopPropagation();
        flagMissingSelections();
        return false;
      }
    });
  } else if (window.console && document.querySelector('[data-purchase-submit]')) {
    console.warn('[product-hero] No cart form found — option validation disabled.');
  }

  // Purchase type radios — `change` covers mouse, touch and keyboard (arrow keys).
  radioInputs.forEach(function (input) {
    input.addEventListener('change', function () {
      if (input.checked) setPurchaseType(input.value);
    });
  });

  // Picking a delivery option implies Subscribe & Save.
  if (sellingPlanSelect) {
    sellingPlanSelect.addEventListener('change', function () { setPurchaseType('subscribe'); });
  }

  // "Subscribe & Save" buttons elsewhere on the page (e.g. the rotation
  // banner) scroll to the purchase options and select Subscribe & Save.
  // Delegated because those sections render after this script runs.
  // Without a purchase selector on the page the link is followed instead
  // (it points at the product with its first selling plan pre-selected).
  document.addEventListener('click', function (e) {
    var cta = e.target.closest && e.target.closest('[data-subscribe-cta]');
    if (!cta || !purchaseRadio || !subscribeInput || subscribeInput.disabled) return;
    e.preventDefault();
    setPurchaseType('subscribe');
    var target = document.getElementById('purchase-options') || purchaseRadio;
    target.scrollIntoView({ behavior: 'smooth', block: 'center' });
    var focusTarget = sellingPlanSelect || subscribeInput;
    try { focusTarget.focus({ preventScroll: true }); } catch (err) {}
  });

  // Quantity +/−
  var qtyMinus = document.querySelector('[data-qty-minus]');
  var qtyPlus  = document.querySelector('[data-qty-plus]');
  if (qtyInput && qtyMinus && qtyPlus) {
    qtyMinus.addEventListener('click', function () {
      var v = parseInt(qtyInput.value, 10);
      if (v > 1) qtyInput.value = v - 1;
    });
    qtyPlus.addEventListener('click', function () {
      var v = parseInt(qtyInput.value, 10);
      if (v < 10) qtyInput.value = v + 1;
    });
  }

  // ── Neutralise Appstle's injected widget ───────────────────────
  // The Appstle Subscriptions app auto-injects its own subscription
  // widget (with duplicate `<input name="selling_plan">` radios) into
  // our cart form. Because its "one-time" radio is checked by default
  // with value="", it was overriding OUR hidden selling_plan input and
  // sending an empty value to /cart/add — collapsing every order to
  // one-time pricing with no discount and no subscription metadata.
  //
  // We use our own purchase-type radios + selling-plan select instead,
  // so the Appstle widget is pure interference. We disable its inputs
  // so the browser skips them at submit time, leaving our hidden input
  // as the sole `selling_plan` field. We also run this observer in case
  // Appstle's script injects the widget late.
  function neutraliseAppstleWidget(scope) {
    var widgets = (scope || document).querySelectorAll('.appstle_sub_widget, [id^="appstle_subscription_widget"]');
    if (!widgets.length) return;
    widgets.forEach(function (w) {
      // Remove the whole widget subtree from the form so nothing in it
      // submits. Leave it in the DOM (outside the form) only if you
      // need to keep Appstle's script happy; the simpler route is
      // to rip it out.
      var inputs = w.querySelectorAll('input[name="selling_plan"], select[name="selling_plan"]');
      inputs.forEach(function (el) {
        el.disabled = true;
        el.removeAttribute('name');
        el.setAttribute('data-appstle-neutralised', 'true');
      });
      w.setAttribute('aria-hidden', 'true');
      w.style.display = 'none';
    });
  }

  neutraliseAppstleWidget();

  // Appstle loads async — if it injects after our init, catch it.
  if (window.MutationObserver) {
    var appstleObserver = new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var added = mutations[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          var node = added[j];
          if (node.nodeType !== 1) continue;
          if (node.matches && (node.matches('.appstle_sub_widget') || node.matches('[id^="appstle_subscription_widget"]'))) {
            neutraliseAppstleWidget(node.parentNode);
          } else if (node.querySelector && node.querySelector('.appstle_sub_widget, [id^="appstle_subscription_widget"]')) {
            neutraliseAppstleWidget(node);
          }
        }
      }
    });
    appstleObserver.observe(document.body, { childList: true, subtree: true });
    // Stop observing after 10s — Appstle always injects within that window.
    setTimeout(function () { appstleObserver.disconnect(); }, 10000);
  }

  // Defensive: on submit, forcibly strip any stray Appstle selling_plan
  // fields one more time in case something re-enabled them.
  if (atcForm) {
    atcForm.addEventListener('submit', function () {
      neutraliseAppstleWidget(atcForm);
    }, true); // capture-phase so this runs before the validation handler
  }

  // ── Init ───────────────────────────────────────────────────────
  render();
  clearAllErrors();

}());
